'use client';
import { useMemo, useRef } from "react";
import Image from "next/image";
import { useParams } from 'next/navigation';
import { LangLink } from "../../i18n/links";
import { defaultLang, SUPPORTED } from "../../config/languages";

const toSlug = (title = "") =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, " ");
const makeExcerpt = (html = "", max = 140) => {
  const text = stripHtml(html).replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "…";
};

const defaultMetaDate = (dateStr = "", lang = "en") => {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? `${dateStr}T00:00:00.000Z`
    : dateStr;
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    const day = String(parsed.getUTCDate()).padStart(2, "0");
    const locale = lang === "sr" ? "sr-Latn" : lang;
    const month = parsed.toLocaleString(locale || "en", { month: "short", timeZone: "UTC" }).toUpperCase();
    return `${day} ${month}`;
  }

  const parts = String(dateStr).split(" ");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")} ${parts[1].slice(0, 3).toUpperCase()}`;
  }
  return dateStr;
};

const localizedSlug = (item, lang, title) =>
  (typeof item.slug === 'object' ? (item.slug[lang] || item.slug.en) : item.slug) || toSlug(title);

const localizedCurrentSlug = (slug, lang) => {
  if (!slug) return "";
  return typeof slug === "object" ? (slug[lang] || slug.en || "") : slug;
};

export default function RelatedContentCarousel({
  sourceItems = [],
  currentId,
  currentSlug,
  maxItems = 8,
  title,
  readMoreLabel = "Read More",
  scrollLeftLabel = "Scroll left",
  scrollRightLabel = "Scroll right",
  basePath = "/blog",
  showTitleWhen = "hasItems",
  showMetaDate = false,
  formatMetaDate = defaultMetaDate,
  getDate = (item, translation) =>
    translation.datePublished ||
    item.datePublished ||
    translation.dateModified ||
    item.dateModified ||
    translation.date ||
    item.date ||
    "",
}) {
  const { lang = defaultLang } = useParams();
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;

  const items = useMemo(() => {
    const currentLocalizedSlug = localizedCurrentSlug(currentSlug, activeLang);
    const enriched = sourceItems.map((item) => {
      const translation =
        (item.translations && item.translations[activeLang]) ||
        (item.translations && item.translations.en) ||
        item;

      const itemTitle = translation.title || item.title || "";
      const description = translation.description || item.description || "";
      const rawContent = translation.content || item.content || "";
      const content = typeof rawContent === 'function' ? "" : rawContent;
      const date = getDate(item, translation);

      return {
        ...item,
        title: itemTitle,
        description,
        slug: localizedSlug(item, activeLang, itemTitle),
        excerpt: item.excerpt || makeExcerpt(content, 140),
        metaDate: formatMetaDate(date, activeLang),
      };
    });

    return enriched
      .filter(
        (item) =>
          (currentId != null ? String(item.id) !== String(currentId) : true) &&
          (currentLocalizedSlug ? item.slug !== currentLocalizedSlug : true)
      )
      .slice(0, maxItems);
  }, [activeLang, currentId, currentSlug, formatMetaDate, getDate, maxItems, sourceItems]);

  const rowRef = useRef(null);
  const scrollBy = (dir) => {
    const node = rowRef.current;
    if (!node) return;
    node.scrollBy({ left: dir * node.clientWidth * 0.9, behavior: "smooth" });
  };

  if (!items.length) return null;

  const shouldShowTitle = showTitleWhen === "moreThanOne" ? items.length > 1 : true;

  return (
    <section className="you-may-also-like">
      {shouldShowTitle && (
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="blog_heading">
              <h3>{title}</h3>
            </div>
          </div>
        </div>
      )}

      <div className="position-relative">
        {items.length > 2 && (
          <button
            type="button"
            aria-label={scrollLeftLabel}
            onClick={() => scrollBy(-1)}
            className="btn btn-link position-absolute start-0 top-50 translate-middle-y d-none d-md-block"
          >
            ‹
          </button>
        )}

        <div
          ref={rowRef}
          className="row flex-nowrap overflow-auto gx-4"
          style={{ scrollSnapType: "x mandatory", scrollBehavior: "smooth" }}
        >
          {items.map((item) => {
            const href = `${basePath}/${item.slug}`;
            return (
              <div
                className="col-lg-6 col-10"
                key={item.id}
                style={{ scrollSnapAlign: "start" }}
              >
                <div className="single_blog">
                  <div className="single_blog_img">
                    <Image src={item.image} alt={item.title} loading="lazy" width={640} height={360} sizes="(max-width: 992px) 80vw, 540px" style={{ width: '100%', height: 'auto' }} />
                    {showMetaDate && <a href="#" className="meta_date">{item.metaDate}</a>}
                  </div>
                  <div className="single_blog_content">
                    <h3>
                      <LangLink to={href}>{item.title}</LangLink>
                    </h3>
                    <p>{item.description}</p>
                    <LangLink to={href} className="readmore_btn">
                      {readMoreLabel} <i className="far fa-angle-right"></i>
                    </LangLink>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 2 && (
          <button
            type="button"
            aria-label={scrollRightLabel}
            onClick={() => scrollBy(1)}
            className="btn btn-link position-absolute end-0 top-50 translate-middle-y d-none d-md-block"
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
}
